import { SiteSiteTypeEnum, type Site, type SiteCreate, type SiteUpdate } from '@app/types/sdk';
import { isUUIDString } from '@app/utils/uuid';

export type SiteType = NonNullable<Site['siteType']>;

export interface KeyValuePair {
  key: string;
  value: string;
}

type SiteLabels = Record<string, string>;
type SiteCreateWithLabels = SiteCreate & { labels?: SiteLabels };
type SiteUpdateWithLabels = SiteUpdate & { labels?: SiteLabels };

export const SITE_TYPE_OPTIONS = [
  { value: SiteSiteTypeEnum.OnPrem, label: 'OnPrem' },
  { value: SiteSiteTypeEnum.AwsCloud, label: 'AWS Cloud' },
  { value: SiteSiteTypeEnum.AzureCloud, label: 'Azure Cloud' }
] as const satisfies ReadonlyArray<{ value: SiteType; label: string }>;

export const normalizeSiteType = (raw?: string | null): SiteType | null => {
  const normalized = raw?.trim().toLowerCase();
  if (!normalized) {
    return null;
  }
  if (normalized === SiteSiteTypeEnum.OnPrem.toLowerCase()) {
    return SiteSiteTypeEnum.OnPrem;
  }
  if (normalized === SiteSiteTypeEnum.AwsCloud.toLowerCase() || normalized === 'aws') {
    return SiteSiteTypeEnum.AwsCloud;
  }
  if (normalized === SiteSiteTypeEnum.AzureCloud.toLowerCase()) {
    return SiteSiteTypeEnum.AzureCloud;
  }
  return null;
};

const AZURE_LOCATION_PATTERN = /^(?:[a-z0-9]|[a-z0-9][a-z0-9-]{0,62}[a-z0-9])$/;

export const normalizeAzureLocation = (raw?: string | null): string => raw?.trim().toLowerCase() ?? '';

const isDisallowedAzureLocation = (location: string): boolean =>
  location === 'all-locations' ||
  location === 'global' ||
  location.includes('euap') ||
  location.includes('stg') ||
  location.includes('stage');

export const inferSiteType = (
  site?: Pick<Site, 'siteType' | 'awsRegion' | 'awsAccountId' | 'azureSubscriptionId' | 'azureLocation'> | null
): SiteType | null => {
  const hasExplicitType = typeof site?.siteType === 'string' && site.siteType.trim().length > 0;
  const explicitType = hasExplicitType ? normalizeSiteType(site.siteType) : null;
  const hasAwsScope = Boolean(site?.awsRegion?.trim() || site?.awsAccountId?.trim());
  const hasAzureScope = Boolean(site?.azureSubscriptionId?.trim() || site?.azureLocation?.trim());

  if (hasExplicitType) {
    if (!explicitType) {
      return null;
    }
    if (explicitType === SiteSiteTypeEnum.OnPrem) {
      return hasAwsScope || hasAzureScope ? null : explicitType;
    }
    if (explicitType === SiteSiteTypeEnum.AwsCloud) {
      return hasAzureScope ? null : explicitType;
    }
    return hasAwsScope ? null : explicitType;
  }

  if (hasAwsScope && hasAzureScope) {
    return null;
  }
  if (hasAzureScope) {
    return SiteSiteTypeEnum.AzureCloud;
  }
  if (hasAwsScope) {
    return SiteSiteTypeEnum.AwsCloud;
  }
  return SiteSiteTypeEnum.OnPrem;
};

export const siteTopologyV2ViewId = (
  site?: Pick<Site, 'siteType' | 'awsRegion' | 'awsAccountId' | 'azureSubscriptionId' | 'azureLocation'> | null
): string | null => {
  const siteType = inferSiteType(site);
  if (siteType === SiteSiteTypeEnum.AwsCloud) {
    return 'aws_cloud_overview';
  }
  if (siteType === SiteSiteTypeEnum.AzureCloud) {
    return 'azure_cloud_overview';
  }
  return null;
};

export interface SiteFormValues {
  name: string;
  siteType: SiteType;
  awsRegion: Site['awsRegion'];
  awsAccountId: Site['awsAccountId'];
  azureSubscriptionId: Site['azureSubscriptionId'];
  azureLocation: Site['azureLocation'];
  notes: Site['notes'];
  address: NonNullable<Site['address']>;
  labels: KeyValuePair[];
}

export type SiteLocationFieldErrors = Partial<
  Record<'postalCode' | 'country' | 'azureSubscriptionId' | 'azureLocation', string>
>;

export const validateSiteLocationFields = (values: SiteFormValues): SiteLocationFieldErrors => {
  if (values.siteType === SiteSiteTypeEnum.AzureCloud) {
    const errors: SiteLocationFieldErrors = {};
    const subscriptionId = values.azureSubscriptionId?.trim() ?? '';
    if (!subscriptionId) {
      errors.azureSubscriptionId = 'Azure subscription ID is required for Azure Cloud sites.';
    } else if (!isUUIDString(subscriptionId)) {
      errors.azureSubscriptionId = 'Enter a valid Azure subscription UUID.';
    }
    const azureLocation = normalizeAzureLocation(values.azureLocation);
    if (!azureLocation) {
      errors.azureLocation = 'Azure location code is required for Azure Cloud sites.';
    } else if (!AZURE_LOCATION_PATTERN.test(azureLocation) || isDisallowedAzureLocation(azureLocation)) {
      errors.azureLocation = 'Enter a canonical Azure location code such as eastus.';
    }
    return errors;
  }
  if (values.siteType !== SiteSiteTypeEnum.OnPrem) {
    return {};
  }

  const errors: SiteLocationFieldErrors = {};
  if (!values.address.postalCode?.trim()) {
    errors.postalCode = 'Postal code is required for OnPrem sites.';
  }
  if (!values.address.country?.trim()) {
    errors.country = 'Country is required for OnPrem sites.';
  }
  return errors;
};

const normalizeAddress = (address: SiteFormValues['address']) => {
  const next: Partial<SiteFormValues['address']> = { ...address };
  (Object.keys(next) as Array<keyof SiteFormValues['address']>).forEach((key) => {
    const value = next[key];
    if (!value || value.trim().length === 0) {
      delete next[key];
    } else {
      next[key] = value.trim();
    }
  });
  return Object.keys(next).length > 0 ? next : undefined;
};

export const labelPairsToRecord = (pairs: KeyValuePair[]): SiteLabels =>
  pairs.reduce<SiteLabels>((acc, pair) => {
    const trimmedKey = pair.key.trim();
    if (trimmedKey) {
      acc[trimmedKey] = pair.value.trim();
    }
    return acc;
  }, {});

export const toSiteCreatePayload = (values: SiteFormValues) => {
  const labels = labelPairsToRecord(values.labels);
  return {
    name: values.name,
    siteType: values.siteType,
    awsRegion: values.siteType === SiteSiteTypeEnum.AwsCloud ? values.awsRegion?.trim() || undefined : undefined,
    awsAccountId: values.siteType === SiteSiteTypeEnum.AwsCloud ? values.awsAccountId?.trim() || undefined : undefined,
    azureSubscriptionId:
      values.siteType === SiteSiteTypeEnum.AzureCloud ? values.azureSubscriptionId?.trim() || undefined : undefined,
    azureLocation:
      values.siteType === SiteSiteTypeEnum.AzureCloud ? normalizeAzureLocation(values.azureLocation) || undefined : undefined,
    notes: values.notes && values.notes.trim().length > 0 ? values.notes.trim() : undefined,
    address: values.siteType === SiteSiteTypeEnum.OnPrem ? normalizeAddress(values.address) : undefined,
    ...(Object.keys(labels).length > 0 ? { labels } : {})
  } satisfies SiteCreateWithLabels;
};

export const toSiteUpdatePayload = (values: SiteFormValues) => {
  const labels = labelPairsToRecord(values.labels);
  return {
    name: values.name,
    siteType: values.siteType,
    awsRegion: values.siteType === SiteSiteTypeEnum.AwsCloud ? values.awsRegion?.trim() || undefined : undefined,
    awsAccountId: values.siteType === SiteSiteTypeEnum.AwsCloud ? values.awsAccountId?.trim() || undefined : undefined,
    azureSubscriptionId:
      values.siteType === SiteSiteTypeEnum.AzureCloud ? values.azureSubscriptionId?.trim() || undefined : undefined,
    azureLocation:
      values.siteType === SiteSiteTypeEnum.AzureCloud ? normalizeAzureLocation(values.azureLocation) || undefined : undefined,
    notes: values.notes && values.notes.trim().length > 0 ? values.notes.trim() : undefined,
    address: values.siteType === SiteSiteTypeEnum.OnPrem ? normalizeAddress(values.address) ?? {} : undefined,
    ...(Object.keys(labels).length > 0 ? { labels } : {})
  } satisfies SiteUpdateWithLabels;
};
